import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { apiFetch } from '../../lib/api';
import { formatAud, formatDay, formatUnits } from '../../lib/format';
import { useSession } from '../../lib/session';
import { Board, Empty, Spinner, Stat } from '../../components/ui';

interface Summary {
  timezone: string;
  days: number;
  scope: 'all' | string[];
  totals: { orderCount: number; revenueCents: number; discountCents: number; unitsSold: number };
  daily: { day: string; orderCount: number; revenueCents: number }[];
  weekly: { weekStart: string; orderCount: number; revenueCents: number }[];
  topVegetables: { name: string; unitsSold: number; revenueCents: number; lineCount: number }[];
  perStore: { storeId: string; storeName: string; orderCount: number; revenueCents: number }[];
}

interface Store {
  id: string;
  name: string;
  canManage?: boolean;
}

const RANGES = [
  { days: 7, label: '7 days' },
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
];

export function Statistics() {
  const session = useSession();
  const [days, setDays] = useState(30);
  const [storeId, setStoreId] = useState<string>('');

  const stores = useQuery({
    queryKey: ['stores'],
    queryFn: () => apiFetch<Store[]>('/api/stores'),
  });
  const summary = useQuery({
    queryKey: ['summary', days, storeId],
    queryFn: () =>
      apiFetch<Summary>(`/api/reports/summary?days=${days}${storeId ? `&storeId=${storeId}` : ''}`),
  });

  const canSeeEverything = session.data?.capabilities.manageAnyStore ?? false;
  const selectable = (stores.data ?? []).filter((store) => canSeeEverything || store.canManage);

  if (summary.isPending) return <Spinner label="Adding up the takings" />;
  if (summary.isError) return <Empty title="Cannot load statistics" body={summary.error.message} />;

  const data = summary.data;
  const peakRevenue = Math.max(1, ...data.daily.map((row) => row.revenueCents));
  const busiest = Math.max(1, ...data.topVegetables.map((row) => row.unitsSold));

  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl text-ink-950">Statistics</h1>
          <p className="mt-1.5 text-[14px] text-ink-500">
            Days and weeks are counted in {data.timezone.replace('_', ' ')} time, so an evening sale
            lands on the right day.
          </p>
        </div>

        <div className="flex flex-wrap gap-2">
          {RANGES.map((range) => (
            <button
              key={range.days}
              type="button"
              onClick={() => setDays(range.days)}
              className={`sign rounded-[3px] border px-3 py-1.5 text-[11px] ${
                days === range.days
                  ? 'border-ink-900 bg-ink-900 text-paper-50'
                  : 'border-paper-300 bg-paper-50 text-ink-700 hover:border-paper-400'
              }`}
            >
              {range.label}
            </button>
          ))}
        </div>
      </div>

      {selectable.length > 1 && (
        <div className="mt-5 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setStoreId('')}
            className={`sign rounded-[3px] border px-3 py-1.5 text-[11px] ${
              storeId === ''
                ? 'border-leaf-700 bg-leaf-600 text-paper-50'
                : 'border-paper-300 bg-paper-50 text-ink-700'
            }`}
          >
            {canSeeEverything ? 'All stores' : 'My stores'}
          </button>
          {selectable.map((store) => (
            <button
              key={store.id}
              type="button"
              onClick={() => setStoreId(store.id)}
              className={`sign rounded-[3px] border px-3 py-1.5 text-[11px] ${
                storeId === store.id
                  ? 'border-leaf-700 bg-leaf-600 text-paper-50'
                  : 'border-paper-300 bg-paper-50 text-ink-700'
              }`}
            >
              {store.name}
            </button>
          ))}
        </div>
      )}

      <Board className="mt-6 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <Stat
          tone="board"
          label="Takings"
          value={formatAud(data.totals.revenueCents)}
          sub={`over ${data.days} days`}
        />
        <Stat tone="board" label="Sales" value={data.totals.orderCount} sub="completed purchases" />
        <Stat
          tone="board"
          label="Units sold"
          value={formatUnits(data.totals.unitsSold)}
          sub="across all lines"
        />
        <Stat
          tone="board"
          label="Given away in deals"
          value={formatAud(data.totals.discountCents)}
          sub="versus base rates"
        />
      </Board>

      {data.totals.orderCount === 0 ? (
        <div className="mt-6">
          <Empty
            title="No sales in this period"
            body="Once something sells, the breakdown appears here."
          />
        </div>
      ) : (
        <div className="mt-6 grid gap-6 lg:grid-cols-2">
          <section className="tag p-5">
            <h2 className="sign text-[11px] text-ink-700">Most in demand</h2>
            <p className="mt-1 text-[13px] text-ink-500">
              Ranked by units sold — how much left the shelf, not which happens to be dearest.
            </p>
            <ul className="mt-4 space-y-3">
              {data.topVegetables.map((row) => (
                <li key={row.name}>
                  <div className="flex items-baseline justify-between gap-3 text-[14px]">
                    <span className="text-ink-950">{row.name}</span>
                    <span className="tabular text-ink-700">{formatUnits(row.unitsSold)}</span>
                  </div>
                  <div className="mt-1 h-2 overflow-hidden rounded-[2px] bg-paper-200">
                    <div
                      className="h-full bg-leaf-500"
                      style={{ width: `${Math.max(3, (row.unitsSold / busiest) * 100)}%` }}
                    />
                  </div>
                  <div className="tabular mt-1 text-[12px] text-ink-500">
                    {formatAud(row.revenueCents)} from {row.lineCount} line
                    {row.lineCount === 1 ? '' : 's'}
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section className="tag p-5">
            <h2 className="sign text-[11px] text-ink-700">Daily takings</h2>
            <ul className="mt-4 space-y-2">
              {data.daily.slice(0, 14).map((row) => (
                <li key={row.day} className="flex items-center gap-3 text-[13px]">
                  <span className="w-24 shrink-0 text-ink-500">{formatDay(row.day)}</span>
                  <span className="h-3 flex-1 overflow-hidden rounded-[2px] bg-paper-200">
                    <span
                      className="block h-full bg-ochre-500"
                      style={{ width: `${Math.max(2, (row.revenueCents / peakRevenue) * 100)}%` }}
                    />
                  </span>
                  <span className="tabular w-20 shrink-0 text-right text-ink-900">
                    {formatAud(row.revenueCents)}
                  </span>
                </li>
              ))}
            </ul>
          </section>

          <section className="tag p-5">
            <h2 className="sign text-[11px] text-ink-700">By week</h2>
            <ul className="mt-4 space-y-2">
              {data.weekly.map((row) => (
                <li
                  key={row.weekStart}
                  className="flex items-baseline justify-between gap-3 text-[14px]"
                >
                  <span className="text-ink-700">Week of {formatDay(row.weekStart)}</span>
                  <span className="tabular text-ink-950">
                    {formatAud(row.revenueCents)}
                    <span className="ml-2 text-[12px] text-ink-500">{row.orderCount} sales</span>
                  </span>
                </li>
              ))}
            </ul>
          </section>

          {data.perStore.length > 1 && (
            <section className="tag p-5">
              <h2 className="sign text-[11px] text-ink-700">By store</h2>
              <ul className="mt-4 space-y-2">
                {data.perStore.map((row) => (
                  <li
                    key={row.storeId}
                    className="flex items-baseline justify-between gap-3 text-[14px]"
                  >
                    <span className="text-ink-700">{row.storeName}</span>
                    <span className="tabular text-ink-950">
                      {formatAud(row.revenueCents)}
                      <span className="ml-2 text-[12px] text-ink-500">{row.orderCount} sales</span>
                    </span>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      )}
    </div>
  );
}
